import { ChainId } from '@dcl/schemas/dist/dapps/chain-id';
import { fork, race, take } from 'redux-saga/effects';
import { FINISHED_STATUS, TransactionStatus, FAILED_STATUS, SUCCESS_STATUS, CrossChainProviderType, TRANSACTION_ACTION_FLAG } from './types';
import { FETCH_TRANSACTION_FAILURE, FETCH_TRANSACTION_REQUEST, FETCH_TRANSACTION_SUCCESS, REPLACE_TRANSACTION_SUCCESS, UPDATE_TRANSACTION_STATUS } from './actions';
export function buildActionRef(transaction) {
    const { actionType, withReceipt, isCrossChain, payload } = transaction;
    let transactionPayload;
    if (isCrossChain &&
        transaction.toChainId !== undefined &&
        transaction.requestId !== undefined &&
        transaction.crossChainProviderType !== undefined) {
        transactionPayload = buildCrossChainTransactionFromPayload(transaction.chainId, transaction.toChainId, transaction.hash, transaction.requestId, payload, transaction.crossChainProviderType);
    }
    else {
        const buildFunction = withReceipt
            ? buildTransactionWithReceiptPayload
            : buildTransactionPayload;
        transactionPayload = buildFunction(transaction.chainId, transaction.hash, payload, transaction.chainId);
    }
    return {
        type: actionType,
        payload: transactionPayload
    };
}
export function isTransactionAction(action) {
    return Boolean(action.payload && action.payload[TRANSACTION_ACTION_FLAG]);
}
export function getTransactionPayloadFromAction(action) {
    return action.payload[TRANSACTION_ACTION_FLAG];
}
export function isTransactionActionCrossChain(action) {
    if (isTransactionAction(action)) {
        const { chainId, toChainId } = getTransactionPayloadFromAction(action);
        return chainId !== toChainId && toChainId !== undefined;
    }
    return false;
}
export function getTransactionFromAction(action) {
    const transactionPayload = action.payload[TRANSACTION_ACTION_FLAG];
    const isCrossChain = transactionPayload.chainId !== transactionPayload.toChainId;
    return {
        events: [],
        hash: transactionPayload.hash,
        timestamp: Date.now(),
        from: transactionPayload.from?.toLowerCase() || '',
        actionType: action.type,
        url: getTransactionHref({
            txHash: transactionPayload.hash,
            address: transactionPayload.from,
            crossChainProviderType: transactionPayload.crossChainProviderType
        }, transactionPayload.chainId),
        isCrossChain,
        crossChainProviderType: transactionPayload.crossChainProviderType,
        requestId: transactionPayload.requestId,
        withReceipt: transactionPayload.withReceipt,
        payload: transactionPayload.payload,
        chainId: transactionPayload.chainId,
        toChainId: transactionPayload.toChainId,
        // these always start as null, and they get updated by the saga
        status: null,
        nonce: null,
        replacedBy: null
    };
}
export function getTransactionHashFromAction(action) {
    return getTransactionPayloadFromAction(action).hash;
}
export function getTransactionAddressFromAction(action) {
    return getTransactionPayloadFromAction(action).from;
}
export function buildTransactionPayload(chainId, hash, payload = {}, toChainId) {
    return {
        [TRANSACTION_ACTION_FLAG]: {
            chainId,
            toChainId: toChainId ?? chainId,
            hash,
            payload
        }
    };
}
export function buildCrossChainTransactionFromPayload(chainId, toChainId, hash, requestId, payload = {}, providerType = CrossChainProviderType.SQUID) {
    const txPayload = buildTransactionPayload(chainId, hash, payload, toChainId);
    txPayload[TRANSACTION_ACTION_FLAG].requestId = requestId;
    txPayload[TRANSACTION_ACTION_FLAG].crossChainProviderType = providerType;
    return txPayload;
}
export function buildTransactionWithReceiptPayload(chainId, hash, payload = {}) {
    const txPayload = buildTransactionPayload(chainId, hash, payload, chainId);
    txPayload[TRANSACTION_ACTION_FLAG].withReceipt = true;
    return txPayload;
}
export function buildTransactionWithFromPayload(chainId, hash, from, payload = {}) {
    const txPayload = buildTransactionPayload(chainId, hash, payload, chainId);
    txPayload[TRANSACTION_ACTION_FLAG].from = from;
    return txPayload;
}
export function getTransactionHref({ txHash, address, blockNumber, crossChainProviderType }, network) {
    if (crossChainProviderType) {
        switch (crossChainProviderType) {
            case CrossChainProviderType.SQUID:
                return `https://axelarscan.io/gmp/${txHash}`;
            default:
                console.error('getTransactionHref: Unknown cross chain provider');
                return '';
        }
    }
    const pathname = address
        ? `/address/${address}`
        : blockNumber
            ? `/block/${blockNumber}`
            : `/tx/${txHash}`;
    return `${getTransactionOrigin(network)}${pathname}`;
}
export function getTransactionOrigin(chainId = ChainId.ETHEREUM_MAINNET) {
    switch (chainId) {
        case ChainId.ETHEREUM_ROPSTEN:
            return 'https://ropsten.etherscan.io';
        case ChainId.ETHEREUM_RINKEBY:
            return 'https://rinkeby.etherscan.io';
        case ChainId.ETHEREUM_GOERLI:
            return 'https://goerli.etherscan.io';
        case ChainId.ETHEREUM_SEPOLIA:
            return 'https://sepolia.etherscan.io';
        case ChainId.MATIC_MAINNET:
            return 'https://explorer-mainnet.maticvigil.com';
        case ChainId.MATIC_MUMBAI:
            return 'https://explorer-mumbai.maticvigil.com';
        default:
            return 'https://etherscan.io';
    }
}
export function isPending(status) {
    return !FINISHED_STATUS.includes(status);
}
export function hasFailed(status) {
    return FAILED_STATUS.includes(status);
}
export function hasSucceeded(status) {
    return SUCCESS_STATUS.includes(status);
}
/**
 * Waits for a transaction to be completed.
 *
 * @param txHash - The hash of the transaction to wait for.
 */
export function* waitForTx(txHash) {
    let txHashToWaitFor = txHash;
    while (true) {
        const { success, failure } = yield race({
            success: take(FETCH_TRANSACTION_SUCCESS),
            failure: take(FETCH_TRANSACTION_FAILURE)
        });
        if (success?.payload.transaction.hash === txHashToWaitFor) {
            break;
        }
        else if (failure &&
            failure.payload.transaction.hash === txHashToWaitFor) {
            if (failure.payload.status === TransactionStatus.DROPPED) {
                let continueWaiting = true;
                // If the transaction was dropped, follow the procedure to check what to do
                while (true) {
                    const { replace, fetchAgain, update } = yield race({
                        replace: take(REPLACE_TRANSACTION_SUCCESS),
                        fetchAgain: take(FETCH_TRANSACTION_REQUEST),
                        update: take(UPDATE_TRANSACTION_STATUS)
                    });
                    if (fetchAgain && fetchAgain.payload.hash === txHashToWaitFor) {
                        // Re start the transaction fetching process.
                        txHashToWaitFor = fetchAgain.payload.hash;
                        continueWaiting = true;
                        break;
                    }
                    else if (replace && replace.payload.hash === txHashToWaitFor) {
                        // The transaction hash was replaced for another one, track the other transaction hash.
                        txHashToWaitFor = replace.payload.replaceBy;
                        continueWaiting = true;
                        break;
                    }
                    else if (update &&
                        update.payload.hash === txHashToWaitFor &&
                        update.payload.status === TransactionStatus.REPLACED) {
                        // A new hash wasn't found, but the transaction was replaced. We should consider the wait finished because we don't know if it failed.
                        continueWaiting = false;
                        break;
                    }
                }
                if (continueWaiting) {
                    continue;
                }
                break;
            }
            else {
                throw new Error(`The transaction ${txHash} failed to be mined. The status is ${failure.payload.status}.`);
            }
        }
    }
}
export const takeEverySuccessfulTx = (actionType, worker, ...args) => fork(function* () {
    while (true) {
        const action = yield take(FETCH_TRANSACTION_SUCCESS);
        if (action.payload.transaction.actionType === actionType) {
            yield fork(worker, ...args.concat(action));
        }
    }
});
//# sourceMappingURL=utils.js.map